import { useEffect, useState } from "react";
import { LineItemSearchResult } from "@rebuild/shared";
import RestAPI from "../utils/restAPI";

/**
 * Debounced search against the apiserver line item template search endpoint (GET /rest/tools/lineItems/search). Returns
 * one row per matching permutation, each enriched with its xactimate cat/sel. An empty search string yields no results.
 */
export const useLineItemTemplateSearch = (searchString: string, debounceMs = 250) => {
  const [results, setResults] = useState<LineItemSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const trimmed = searchString.trim();
    if (!trimmed || trimmed.length < 3) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;

    const handle = setTimeout(async () => {
      try {
        // fetchRebuildAPI does not url-encode GET params, so build the encoded path ourselves.
        const path = `/rest/tools/lineItems/search?searchString=${encodeURIComponent(trimmed)}`;
        const data = await RestAPI.fetchRebuildAPI(path, "GET", {}, "data", true, false);
        if (!cancelled) {
          setResults(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchString, debounceMs]);

  return { results, isLoading };
};
